// shared-components/features/transitions/PageTransition.jsx
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { useAnimation } from "@shared/context/AnimationContext";

/**
 * Page transition wrapper component
 * Wrap your page content with this to get smooth transitions between routes
 */
function PageTransition({
  children,
  transitionKey,
  mode = "wait",
  transition = "fade",
}) {
  // Ensure transition is always a valid string
  const safeTransition =
    transition && typeof transition === "string" ? transition : "fade";
  const location = useLocation();
  const { enabled, pageTransitions, reducedMotion, duration, easing } =
    useAnimation();

  // If animations disabled or reduced motion, render without animations
  if (!enabled || !pageTransitions || reducedMotion) {
    return <>{children}</>;
  }

  // The key used for AnimatePresence - defaults to pathname
  const key = transitionKey || location.pathname;

  // Pre-defined transition variants
  const transitions = {
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    },
    slide: {
      initial: { opacity: 0, x: 100 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -100 },
    },
    slideUp: {
      initial: { opacity: 0, y: 50 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -50 },
    },
    scale: {
      initial: { opacity: 0, scale: 0.9 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 1.1 },
    },
    flip: {
      initial: { opacity: 0, rotateY: 90 },
      animate: { opacity: 1, rotateY: 0 },
      exit: { opacity: 0, rotateY: -90 },
    },
  };

  // Select the transition variant
  const selectedTransition = transitions[safeTransition] || transitions.fade;

  // Create transition settings with safe values
  const transitionSettings = {
    duration: typeof duration === "number" ? duration : 0.3,
    ease: typeof easing === "string" ? easing : "easeInOut",
  };

  return (
    <AnimatePresence mode={mode} initial={false}>
      <motion.div
        key={key}
        initial={selectedTransition.initial}
        animate={selectedTransition.animate}
        exit={selectedTransition.exit}
        transition={transitionSettings}
        className="w-full h-full"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

PageTransition.propTypes = {
  children: PropTypes.node.isRequired,
  transitionKey: PropTypes.string,
  mode: PropTypes.oneOf(["wait", "sync", "popLayout"]),
  transition: PropTypes.oneOf(["fade", "slide", "slideUp", "scale", "flip"]),
};

export default PageTransition;
